import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  AttachMoney as AttachMoneyIcon,
  Mouse as ClickIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  ViewModule as AdSetIcon,
  Article as AdsIcon,
} from '@mui/icons-material';
import FacebookAdsService, {
  FacebookConnection,
  FacebookStats,
} from '../../services/facebookAdsService';
import { format } from 'date-fns';

const FacebookAdsDashboard: React.FC = () => {
  const [connections, setConnections] = useState<FacebookConnection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<FacebookConnection | null>(null);
  const [stats, setStats] = useState<FacebookStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  });

  useEffect(() => {
    loadConnections();
  }, []);

  useEffect(() => {
    if (selectedConnection) {
      loadData();
    }
  }, [selectedConnection, dateRange]);

  const loadConnections = async () => {
    try {
      const response = await FacebookAdsService.listConnections();
      if (response.success && response.data) {
        setConnections(response.data);
        if (response.data.length > 0 && !selectedConnection) {
          setSelectedConnection(response.data[0]);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar conexões:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    if (!selectedConnection) return;

    setLoading(true);
    try {
      const statsResponse = await FacebookAdsService.getStats(selectedConnection.account_id.toString(), {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });

      if (statsResponse.success && statsResponse.data) {
        setStats(statsResponse.data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (type: 'full' | 'incremental') => {
    if (!selectedConnection) return;

    setSyncing(true);
    try {
      const response =
        type === 'full'
          ? await FacebookAdsService.syncFull(selectedConnection.account_id.toString())
          : await FacebookAdsService.syncIncremental(selectedConnection.account_id.toString());

      if (response.success) {
        setTimeout(() => {
          loadData();
        }, 2000);
      }
    } catch (error) {
      console.error('Erro ao sincronizar:', error);
    } finally {
      setSyncing(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  if (loading && !stats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (connections.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Nenhuma conexão configurada. Configure uma conexão na página de Conexões.
            </Alert>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Header com seleção de conta e ações */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <FormControl fullWidth>
            <InputLabel>Conta de Anúncios</InputLabel>
            <Select
              value={selectedConnection?.account_id || ''}
              onChange={(e) => {
                const value = e.target.value;
                const conn = connections.find((c) => c.account_id === parseInt(value.toString()));
                setSelectedConnection(conn || null);
              }}
              label="Conta de Anúncios"
            >
              {connections.map((conn) => (
                <MenuItem key={conn.account_id} value={conn.account_id}>
                  {conn.account_name || `Conta ${conn.account_id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={() => handleSync('incremental')}
            disabled={syncing}
            size="small"
          >
            Sincronizar
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
            size="small"
          >
            Atualizar
          </Button>
        </Stack>
      </Box>

      {/* Filtro de Datas */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Data Inicial"
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Data Final"
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const today = format(new Date(), 'yyyy-MM-dd');
                    const yesterday = format(new Date(Date.now() - 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
                    setDateRange({ startDate: yesterday, endDate: yesterday });
                  }}
                >
                  Ontem
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const today = format(new Date(), 'yyyy-MM-dd');
                    const last7Days = format(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
                    setDateRange({ startDate: last7Days, endDate: today });
                  }}
                >
                  7 dias
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const today = format(new Date(), 'yyyy-MM-dd');
                    const last30Days = format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
                    setDateRange({ startDate: last30Days, endDate: today });
                  }}
                >
                  30 dias
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const today = format(new Date(), 'yyyy-MM-dd');
                    const last90Days = format(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
                    setDateRange({ startDate: last90Days, endDate: today });
                  }}
                >
                  90 dias
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Cards de Métricas */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AttachMoneyIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2" color="text.secondary">
                    Gasto Total
                  </Typography>
                </Box>
                <Typography variant="h5">{formatCurrency(stats.totals.spend)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ClickIcon sx={{ mr: 1, color: 'info.main' }} />
                  <Typography variant="body2" color="text.secondary">
                    Cliques
                  </Typography>
                </Box>
                <Typography variant="h5">{formatNumber(stats.totals.clicks)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUpIcon sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="body2" color="text.secondary">
                    ROAS
                  </Typography>
                </Box>
                <Typography variant="h5">{stats.metrics.roas.toFixed(2)}x</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PeopleIcon sx={{ mr: 1, color: 'warning.main' }} />
                  <Typography variant="body2" color="text.secondary">
                    Leads
                  </Typography>
                </Box>
                <Typography variant="h5">{formatNumber(stats.counts.leads)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CampaignIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2" color="text.secondary">
                    Campanhas
                  </Typography>
                </Box>
                <Typography variant="h5">{formatNumber(stats.counts.campaigns)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AdSetIcon sx={{ mr: 1, color: 'info.main' }} />
                  <Typography variant="body2" color="text.secondary">
                    AdSets
                  </Typography>
                </Box>
                <Typography variant="h5">{formatNumber(stats.counts.adsets)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AdsIcon sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="body2" color="text.secondary">
                    Anúncios
                  </Typography>
                </Box>
                <Typography variant="h5">{formatNumber(stats.counts.ads)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUpIcon sx={{ mr: 1, color: 'warning.main' }} />
                  <Typography variant="body2" color="text.secondary">
                    Conversões
                  </Typography>
                </Box>
                <Typography variant="h5">{formatNumber(stats.totals.conversions)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {syncing && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Sincronizando dados... Aguarde alguns instantes.
        </Alert>
      )}
    </Box>
  );
};

export default FacebookAdsDashboard;


